import { useContext, useRef, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';

/**
 * Base64FileField — la pièce jointe d'un formulaire PUBLIC, posée dans le store.
 *
 * Pourquoi du JS et pas du YAML : aucun champ reactive-json ne fait remonter le
 * CONTENU d'un fichier choisi. `FileField` ne pose qu'un `<input type="file">`
 * inerte — il n'envoie rien —, et la médiathèque (`MediaPickerField`) exige un
 * compte Drupal, donc reste hors de portée d'un visiteur anonyme. Ce champ est
 * exactement ce chaînon : il lit le fichier avec `FileReader` et écrit
 * `{name, type, size, data}` à un `dataLocation`, que le YAML envoie ensuite
 * comme n'importe quelle autre valeur — un seul envoi, donc une seule preuve
 * anti-robot.
 *
 * Il ne connaît ni le formulaire, ni l'endpoint, ni le métier. Le contrôle
 * définitif (extension, taille, contenu réellement reniflé) est fait par le
 * serveur : ce qui est vérifié ici l'est pour épargner au visiteur un
 * aller-retour, jamais comme garantie.
 *
 * Props YAML :
 *   dataLocation   – liaison de formulaire standard (reçoit l'objet ou {})
 *   accept         – attribut `accept` de l'input (ex. ".pdf,.docx,image/*")
 *   maxBytes       – taille maximale acceptée côté navigateur (défaut 5 Mo)
 *   messages       – { tooLarge, unreadable } libellés d'erreur
 *   attributes     – sur l'enveloppe
 *   inputAttributes – sur l'input
 *
 * Exemple :
 *   - type: Base64FileField
 *     dataLocation: ~~.lead.attachment
 *     accept: ".pdf,.doc,.docx,.odt,.rtf,.txt,.png,.jpg,.jpeg,.webp"
 */
export const Base64FileField = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const resolve = (valueToEvaluate) =>
        evaluateTemplateValue({ valueToEvaluate, globalDataContext, templateContext });

    const { formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: {},
        globalDataContext,
        templateContext,
    });

    const attributes = useEvaluatedAttributes(props.attributes ?? {});
    const inputAttributes = useEvaluatedAttributes(props.inputAttributes ?? {});
    const accept = resolve(props.accept) || undefined;
    const maxBytes = Number(resolve(props.maxBytes)) || 5242880;
    const messages = resolve(props.messages) ?? {};

    const inputRef = useRef(null);
    const [error, setError] = useState('');

    const onChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            setError('');
            globalDataContext.updateData({}, formDataPath);
            return;
        }

        if (file.size > maxBytes) {
            // Le champ est VIDÉ en même temps que l'erreur est posée : sans ça,
            // un premier fichier valide resterait dans le store et partirait à
            // la place de celui que le visiteur vient de refuser.
            setError(
                messages.tooLarge
                ?? `Ce fichier dépasse ${Math.round(maxBytes / 1048576)} Mo.`,
            );
            globalDataContext.updateData({}, formDataPath);
            if (inputRef.current) inputRef.current.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result ?? '');
            // `readAsDataURL` rend `data:<type>;base64,<charge>` : on ne garde
            // que la charge, le serveur accepte les deux formes.
            const data = result.includes(',') ? result.slice(result.indexOf(',') + 1) : result;
            setError('');
            globalDataContext.updateData(
                { name: file.name, type: file.type, size: file.size, data },
                formDataPath,
            );
        };
        reader.onerror = () => {
            setError(messages.unreadable ?? "Ce fichier n'a pas pu être lu.");
            globalDataContext.updateData({}, formDataPath);
        };
        reader.readAsDataURL(file);
    };

    return (
        <ActionDependant {...props} attributesHolderRef={inputRef}>
            <div {...attributes}>
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    onChange={onChange}
                    {...inputAttributes}
                />
                {/* L'erreur est annoncée sans déplacer le focus : le visiteur
                    vient de quitter le sélecteur de fichiers du système. */}
                <p role="alert" className="lpf-error" hidden={!error}>{error}</p>
            </div>
        </ActionDependant>
    );
};
